import React, { useState } from "react";
import Navbar from "../components/Navbar";
import { Link } from "react-router-dom";
import { CheckCircle, X, Crown, Users, ArrowRight, Sparkle } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Pricing() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);
  const [billing, setBilling] = useState("monthly"); // monthly | annual
  const [eduSeats, setEduSeats] = useState(5);

  const upgrade = async (packageId, quantity = 1) => {
    if (!user) {
      window.location.href = "/login";
      return;
    }
    setLoading(packageId);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/checkout", {
        package_id: packageId,
        origin_url: origin,
        quantity,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout");
        setLoading(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoading(null);
    }
  };

  const plusPackage = billing === "annual" ? "plus_annual" : "plus_monthly";
  const plusPrice = billing === "annual" ? "$59" : "$7.99";
  const plusSuffix = billing === "annual" ? "/ year · save 38%" : "/ month";

  const eduPackage = billing === "annual" ? "edu_annual" : "edu_monthly";
  const eduPerSeat = billing === "annual" ? 49 : 4.99;
  const eduTotal = (eduPerSeat * eduSeats).toFixed(2);
  const eduSuffix = billing === "annual" ? `/ year · ${eduSeats} seats = $${eduTotal}` : `/ seat / month · ${eduSeats} seats = $${eduTotal}`;

  const TIERS = [
    {
      name: "Free",
      price: "$0",
      suffix: "forever",
      desc: "Everything you need to start memorizing with your voice.",
      cta: user?.plan === "free" || !user?.plan
        ? { label: "You're on this plan", disabled: true }
        : { label: "Start for free", href: "/login" },
      features: [
        { yes: true, text: "5 active decks · 50 cards per deck" },
        { yes: true, text: "Hands-free mode with voice commands" },
        { yes: true, text: "Concept match · 50 calls / day" },
        { yes: true, text: "AI hints · 15 / day" },
        { yes: true, text: "AI deck generation · 3 / month" },
        { yes: true, text: "1 document upload / month" },
        { yes: true, text: "Share decks publicly" },
        { yes: false, text: "Smart Review — auto-schedules weak cards" },
        { yes: false, text: "Premium AI voices" },
      ],
    },
    {
      name: "Plus",
      price: plusPrice,
      suffix: plusSuffix,
      desc: "Unlimited study, smart scheduling, premium voices.",
      featured: true,
      cta: user?.plan === "plus"
        ? { label: "You're on Plus", disabled: true }
        : { label: `Upgrade to Plus`, onClick: () => upgrade(plusPackage, 1), loading: loading === plusPackage },
      features: [
        { yes: true, text: "Everything in Free" },
        { yes: true, text: "Unlimited decks &amp; cards" },
        { yes: true, text: "Concept match · 2,000 / day" },
        { yes: true, text: "AI hints · 300 / day" },
        { yes: true, text: "AI deck gen · 50 / month" },
        { yes: true, text: "20 doc uploads / month · 20 MB each" },
        { yes: true, text: "Smart Review — auto-schedules weak cards (coming soon)" },
        { yes: true, text: "Premium OpenAI voices (coming soon)" },
        { yes: true, text: "Weekly progress email · priority support" },
      ],
    },
    {
      name: "Edu",
      price: `$${eduPerSeat}`,
      suffix: eduSuffix,
      desc: "Classrooms and training cohorts — 5 to 50 students.",
      cta: user
        ? { label: `Start Edu · ${eduSeats} seats`, onClick: () => upgrade(eduPackage, eduSeats), loading: loading === eduPackage }
        : { label: "Contact sales", href: "mailto:sales@ekkol.app" },
      features: [
        { yes: true, text: "Everything in Plus, per student" },
        { yes: true, text: "Teacher dashboard · assigned decks" },
        { yes: true, text: "Per-student progress reports" },
        { yes: true, text: "CSV roster import" },
        { yes: true, text: "Shared class deck library" },
        { yes: true, text: "Priority email support" },
        { yes: true, text: "50-student cap · above: contact sales" },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-16 fade-in">
        <div className="text-center mb-12">
          <div className="eyebrow mb-4">Pricing</div>
          <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-[0.95]">
            Voice-first study.<br/>
            <span className="italic text-[color:var(--oxford)]">Radically fair pricing.</span>
          </h1>
          <p className="mt-6 text-[color:var(--ink-muted)] max-w-2xl mx-auto">
            Start free forever. Upgrade when you&apos;re ready to go unlimited. Switch back anytime — no fuss.
          </p>
        </div>

        {/* Monthly / Annual toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex border rule bg-white">
            <button
              onClick={() => setBilling("monthly")}
              className={`px-5 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${billing === "monthly" ? "bg-[color:var(--ink)] text-white" : "hover:bg-[color:var(--surface-alt)]"}`}
              data-testid="billing-toggle-monthly"
            >Monthly</button>
            <button
              onClick={() => setBilling("annual")}
              className={`px-5 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${billing === "annual" ? "bg-[color:var(--ink)] text-white" : "hover:bg-[color:var(--surface-alt)]"}`}
              data-testid="billing-toggle-annual"
            >
              Annual <span className="ml-2 text-[10px] bg-[color:var(--vermilion)] text-white px-1.5 py-0.5 rounded-sm">Save 38%</span>
            </button>
          </div>
        </div>

        {/* Edu seat adjuster */}
        <div className="max-w-md mx-auto mb-10 p-4 bg-white border rule">
          <div className="flex items-center justify-between">
            <div className="text-xs font-mono uppercase tracking-widest text-[color:var(--ink-muted)]">
              Edu seats (5–50)
            </div>
            <div className="font-serif text-2xl">{eduSeats}</div>
          </div>
          <input
            type="range" min="5" max="50" step="1"
            value={eduSeats}
            onChange={(e) => setEduSeats(Number(e.target.value))}
            className="w-full mt-3 accent-[color:var(--ink)]"
            data-testid="edu-seats-slider"
          />
          <div className="text-[10px] font-mono text-[color:var(--ink-muted)] mt-2 flex justify-between">
            <span>5</span><span>Need more? Contact sales.</span><span>50</span>
          </div>
        </div>

        {/* Tiers */}
        <div className="grid md:grid-cols-3 gap-px bg-[color:var(--rule)] border rule">
          {TIERS.map((t) => (
            <div key={t.name} className={`bg-white p-6 md:p-8 relative ${t.featured ? "bg-[color:var(--surface-alt)]" : ""}`} data-testid={`tier-${t.name.toLowerCase()}`}>
              {t.featured && (
                <div className="absolute top-0 left-0 bg-[color:var(--ink)] text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1">Recommended</div>
              )}
              <div className="flex items-baseline gap-2">
                <h2 className="font-serif text-3xl">{t.name}</h2>
                {t.name === "Plus" && <Crown size={18} className="text-[color:var(--vermilion)]" weight="duotone"/>}
                {t.name === "Edu" && <Users size={18} className="text-[color:var(--oxford)]" weight="duotone"/>}
              </div>
              <div className="mt-4">
                <span className="font-serif text-5xl tracking-tight">{t.price}</span>
                <span className="text-xs text-[color:var(--ink-muted)] ml-1">{t.suffix}</span>
              </div>
              <p className="mt-4 text-sm text-[color:var(--ink-muted)] leading-relaxed">{t.desc}</p>

              <ul className="mt-6 space-y-2 mb-8">
                {t.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    {f.yes
                      ? <CheckCircle size={16} weight="fill" className="text-[color:var(--success)] shrink-0 mt-0.5"/>
                      : <X size={16} className="text-[color:var(--ink-faint)] shrink-0 mt-0.5"/>}
                    <span className={f.yes ? "" : "text-[color:var(--ink-faint)] line-through"} dangerouslySetInnerHTML={{__html: f.text}}/>
                  </li>
                ))}
              </ul>

              {t.cta.onClick ? (
                <button
                  onClick={t.cta.onClick}
                  disabled={t.cta.loading || t.cta.disabled}
                  className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-mono uppercase tracking-widest transition-colors ${t.cta.disabled ? "bg-[color:var(--surface-alt)] text-[color:var(--ink-muted)]" : t.featured ? "bg-[color:var(--ink)] text-white hover:bg-[color:var(--ink-2)]" : "border rule hover:bg-[color:var(--surface-alt)]"}`}
                  data-testid={`tier-cta-${t.name.toLowerCase()}`}
                >
                  <Sparkle size={14} weight="fill"/>
                  {t.cta.loading ? "Loading…" : t.cta.label}
                </button>
              ) : t.cta.href ? (
                <a
                  href={t.cta.href}
                  className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-mono uppercase tracking-widest ${t.featured ? "bg-[color:var(--ink)] text-white hover:bg-[color:var(--ink-2)]" : "border rule hover:bg-[color:var(--surface-alt)]"}`}
                  data-testid={`tier-cta-${t.name.toLowerCase()}`}
                >
                  {t.cta.label} <ArrowRight size={14}/>
                </a>
              ) : (
                <button
                  disabled
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-mono uppercase tracking-widest bg-[color:var(--surface-alt)] text-[color:var(--ink-muted)]"
                >
                  {t.cta.label}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Footer notes */}
        <div className="mt-16 max-w-3xl mx-auto text-center space-y-3">
          <p className="text-sm text-[color:var(--ink-muted)]">
            🎓 <strong>Student discount</strong> — email <a href="mailto:hello@ekkol.app" className="link-underline">hello@ekkol.app</a> from your <em>.edu</em> address for 50% off Plus.
          </p>
          <p className="text-sm text-[color:var(--ink-muted)]">
            🏢 <strong>Enterprise / 50+ seats</strong> — SSO, SCORM, custom SLA. <a href="mailto:sales@ekkol.app" className="link-underline">sales@ekkol.app</a>
          </p>
          <p className="text-xs text-[color:var(--ink-faint)] font-mono pt-4 border-t rule">
            All plans · Cancel anytime from your Profile · USD prices · Founding users get 50% off Plus for life 🌟
          </p>
        </div>
      </main>
    </div>
  );
}
